import { useContext, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AuthContext } from '../contexts/AuthContext';

const getBackendUrl = () => process.env.REACT_APP_BACKEND_URL || '';

function normalizeTransfersPayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.transfers)) return payload.transfers;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
}

async function fetchAccountTransfers(params, token) {
  const url = new URL(`${getBackendUrl()}/account_transfers`);
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
  });

  const res = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    const err = new Error(`Account transfers failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }

  const json = await res.json();
  return normalizeTransfersPayload(json);
}

async function createAccountTransfer(body, token) {
  const res = await fetch(`${getBackendUrl()}/account_transfers`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  const text = await res.text();
  let json = {};
  try {
    json = text ? JSON.parse(text) : {};
  } catch {
    json = { error: text || 'Request failed' };
  }

  if (!res.ok) {
    const err = new Error(json.error || json.message || `Create transfer failed: ${res.status}`);
    err.status = res.status;
    err.payload = json;
    throw err;
  }

  return json;
}

async function mutateAccountTransfer(url, method, body, token) {
  const res = await fetch(url, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  const text = await res.text();
  let json = {};
  try {
    json = text ? JSON.parse(text) : {};
  } catch {
    json = { error: text || 'Request failed' };
  }

  if (!res.ok) {
    const err = new Error(json.error || json.message || `${method} transfer failed: ${res.status}`);
    err.status = res.status;
    err.payload = json;
    throw err;
  }

  return json;
}

async function updateAccountTransfer(id, body, token) {
  return mutateAccountTransfer(`${getBackendUrl()}/account_transfers/${id}`, 'PUT', body, token);
}

async function deleteAccountTransfer(id, token) {
  return mutateAccountTransfer(`${getBackendUrl()}/account_transfers/${id}`, 'DELETE', null, token);
}

export function accountTransfersQueryKey({ accountId, paperTrading, since, until }) {
  return [
    'account_transfers',
    accountId != null ? String(accountId) : null,
    paperTrading,
    since || null,
    until || null,
  ];
}

/**
 * List account transfers for deposit-adjusted P&L (JWT auth).
 */
export function useAccountTransfers({
  accountId,
  paperTrading,
  since = null,
  until = null,
  enabled = true,
} = {}) {
  const { user } = useContext(AuthContext);
  const token = user?.token;
  const normalizedAccountId =
    accountId !== undefined && accountId !== null ? String(accountId) : undefined;

  const params = useMemo(() => {
    const p = {};
    if (normalizedAccountId) p.account_id = normalizedAccountId;
    if (paperTrading !== undefined) p.paperTrading = paperTrading;
    if (since) p.since = since;
    if (until) p.until = until;
    return p;
  }, [normalizedAccountId, paperTrading, since, until]);

  const queryKey = accountTransfersQueryKey({
    accountId: normalizedAccountId,
    paperTrading,
    since,
    until,
  });

  return useQuery({
    queryKey,
    queryFn: () => fetchAccountTransfers(params, token),
    enabled: !!token && !!normalizedAccountId && enabled,
    staleTime: 30_000,
  });
}

/**
 * Create a deposit/withdrawal transfer and invalidate transfer + equity caches.
 */
export function useCreateAccountTransfer() {
  const { user } = useContext(AuthContext);
  const token = user?.token;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body) => createAccountTransfer(body, token),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['account_transfers'] });
      queryClient.invalidateQueries({ queryKey: ['performance'] });
      if (variables?.account_id) {
        queryClient.invalidateQueries({
          queryKey: ['account_transfers', String(variables.account_id)],
        });
      }
    },
  });
}

/**
 * Edit an existing transfer (amount, direction, date, note) and refresh dependent charts.
 */
export function useUpdateAccountTransfer() {
  const { user } = useContext(AuthContext);
  const token = user?.token;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...body }) => updateAccountTransfer(id, body, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account_transfers'] });
      queryClient.invalidateQueries({ queryKey: ['performance'] });
    },
  });
}

/**
 * Remove a transfer so its deposit/withdrawal adjustment drops out of P&L%.
 */
export function useDeleteAccountTransfer() {
  const { user } = useContext(AuthContext);
  const token = user?.token;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => deleteAccountTransfer(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account_transfers'] });
      queryClient.invalidateQueries({ queryKey: ['performance'] });
    },
  });
}

export {
  fetchAccountTransfers,
  createAccountTransfer,
  updateAccountTransfer,
  deleteAccountTransfer,
  normalizeTransfersPayload,
};
